import config from '../config';
import {
  customFetch,
  authHeader,
  jsonHeader,
  handleResponse,
  commonRequestOptions,
  subjects,
} from '../helpers';

const endpointPath = config.api.API_URL + '/attribute';

export const attributeService = {
  getAll,
  getById,
  getAncestors,
  getByName,
  getByGlossaryId,
  getByFieldId,
  getFieldCount,
  getAttributeValueCount,
  create,
  update,
  checkDestroy,
  destroy,
};

function getAll(signal) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
    ...commonRequestOptions(),
  };
  return customFetch(`${endpointPath}/all`, requestOptions).then(handleResponse);
}

function getById(id, signal) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
    ...commonRequestOptions(),
  };
  const attributeRequest = customFetch(`${endpointPath}/${id}`, requestOptions, signal).then(
    handleResponse
  );

  // Fetch the attribute and its ancestor chain together and publish currentAttribute and
  // currentAttributeAncestors in the same render, so the breadcrumb's parent crumbs appear with the
  // attribute rather than a step later. The chain is resolved server-side because the parent entity is
  // only known once the attribute has been read — asking for it afterwards would cost a second
  // round trip and reintroduce that flash. The ancestors fetch is non-critical, so its failure must not
  // reject the attribute load (a rejecting attribute request still propagates, preserving the view's
  // not-found handling).
  return Promise.all([
    attributeRequest,
    getAncestors(id, signal).catch(() => ({ entities: [] })),
  ]).then(([res, ancestorsRes]) => {
    subjects.currentAttribute.next(res.attributes[0] || { attribute_id: id });
    subjects.currentAttributeAncestors.next({
      attribute_id: id,
      ancestors: ancestorsRes.entities,
    });
    return res;
  });
}

/**
 * Sends a GET request for an attribute's ancestor chain: the entity it hangs under and every entity
 * above that one, ordered root -> immediate parent. Returns an empty list for an attribute placed
 * directly in a glossary.
 * @param {number|string} id - The attribute id.
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ entities: object[] }>}
 */
function getAncestors(id, signal) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
    ...commonRequestOptions(),
  };
  return customFetch(`${endpointPath}/${id}/ancestors`, requestOptions, signal).then(
    handleResponse
  );
}

/**
 * Sends a GET request for attributes matching a name, compared case-insensitively.
 * @param {string} name - URL-encoded name to match.
 * @param {number} [id] - Attribute to exclude from the result; omit to exclude none.
 * @param {number} [glossaryId] - Restrict the search to this glossary, which is the scope name
 *   uniqueness is enforced over. Omit to search all of Katalogue.
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ attributes: Attribute[] }>}
 */
function getByName(name, id, glossaryId, signal) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
    ...commonRequestOptions(),
  };
  const query = glossaryId ? `?glossary_id=${glossaryId}` : '';
  return customFetch(`${endpointPath}/name/${name}/${id}${query}`, requestOptions, signal).then(
    handleResponse
  );
}

/**
 * Sends a GET request for every attribute in a glossary, ordered by name.
 * Each item carries parent_entity_id, which places it under an entity in the glossary hierarchy;
 * attributes with no parent belong at the glossary root.
 * @param {number} id - glossary_id.
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ attributes: Attribute[] }>}
 */
function getByGlossaryId(id, signal) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
    ...commonRequestOptions(),
  };
  return customFetch(`${endpointPath}/glossary/${id}`, requestOptions, signal).then(handleResponse);
}

function getByFieldId(id, signal) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
    ...commonRequestOptions(),
  };
  return customFetch(`${endpointPath}/field/${id}`, requestOptions, signal)
    .then(handleResponse)
    .then(res => {
      subjects.currentAttribute.next(res.attributes[0]);
      return res;
    });
}

function getFieldCount(id, signal) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
    ...commonRequestOptions(),
  };
  return customFetch(`${endpointPath}/field_count/${id}`, requestOptions, signal).then(
    handleResponse
  );
}

/**
 * Sends a GET request for the number of attribute values per attribute.
 * @param {number|string} [id] - One attribute, or nothing for every attribute that has values.
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ attribute_value_count: Array<{ attribute_id: number, attribute_value_count: number }> }>}
 */
function getAttributeValueCount(id, signal) {
  const requestOptions = {
    method: 'GET',
    headers: authHeader(),
    ...commonRequestOptions(),
  };
  return customFetch(`${endpointPath}/attribute_value_count/${id}`, requestOptions, signal).then(
    handleResponse
  );
}

function create(items, signal) {
  const requestOptions = {
    method: 'POST',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}

function update(items, signal) {
  const requestOptions = {
    method: 'PUT',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}

function checkDestroy(items, signal) {
  const requestOptions = {
    method: 'POST',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}/check_delete`, requestOptions, signal).then(handleResponse);
}

function destroy(items, signal) {
  const requestOptions = {
    method: 'DELETE',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}
